import { Container, TextField } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { ISupportStaffAPI } from '../../fetch/support-staff';
import { useToastContext } from '../../hooks';
import { ISupportStaffRole } from '../../models/support-staff';

export const SupportTeam: FC = () => {
  // Non-state
  const { showToast } = useToastContext();

  // Local state
  const [newElementIndex, setNewElementIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  // If not false, this is the row the user seeks to delete
  const [isDeletingSupportStaffRole, setIsDeletingSupportStaffRole] = useState<
    false | ISupportStaffRole
  >(false);

  // Local state from the network
  const [rows, setRows] = useState<ISupportStaffRole[]>([]);

  const removeRow = (row: ISupportStaffRole): void => {
    setRows((state: ISupportStaffRole[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Role Name',
        verticalAlignment: 'top',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: ISupportStaffRole; index: number } };
        }) => {
          const [supportStaffRoleName, setSupportStaffRoleName] = useState(original.name);

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  className: 'support-staff-role-name-input',
                }}
                autoFocus={index === newElementIndex}
                placeholder="Role Name"
                value={supportStaffRoleName}
                onChange={event => setSupportStaffRoleName(event.target.value)}
                onBlur={() => {
                  // If user clicked away and changes have not been saved, enforce the viewport does not move.
                  if (hasChanges === false) {
                    Promise.resolve().then(() => {
                      const node = document.getElementById(
                        `bank-account-wiring-instructions-${index}`
                      );
                      node?.focus?.();
                      node?.scrollIntoView?.();
                    });
                  }

                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].name !== supportStaffRoleName) {
                      setHasChanges(true);
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].name = supportStaffRoleName;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
    ];
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, rows, isSaving]);

  useEffect(() => {
    fetchOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const units: ISupportStaffRole[] = await ISupportStaffAPI.getAll();
      setRows(units);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : displayFailure('get', 'support team role');
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  return (
    <Page title="Support Team Roles">
      <Container maxWidth={false} disableGutters>
        <AdminTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          hasChanges={hasChanges}
          isSaving={isSaving}
          onAddFocusClass="support-staff-role-input"
          onAdd={() => {
            // Add new empty row
            setRows(state => state.concat(ISupportStaffRole.getDefault()));
            setNewElementIndex(rows.length);
          }}
          onSave={async function upsertSupportStaffRoleType() {
            setIsSaving(true);
            try {
              const newRows: ISupportStaffRole[] = await ISupportStaffAPI.upsert(rows);

              // After successful update, update UI
              setRows(newRows);

              // Reset save button to disabled
              setHasChanges(false);

              showToast('success', 'Successfully saved support team role.');
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : displayFailure('update', 'support team role');
              showToast('error', message);
            } finally {
              setIsSaving(false);
            }
          }}
          onDelete={(original, _index) => {
            if (original.businessUnitTypeId === ISupportStaffRole.getDefault().supportStaffRoleId) {
              removeRow(original);
              showToast('info', 'Removed unsaved support team role.');
            } else {
              setIsDeletingSupportStaffRole(original);
            }
          }}
        />
      </Container>

      {/** ConfirmationDialogue */}
      {!!isDeletingSupportStaffRole && (
        <ConfirmationDialogue
          open
          id="delete-support-staff-role"
          title={`Delete Support Team Role ${isDeletingSupportStaffRole.name}`}
          onClose={() => setIsDeletingSupportStaffRole(false)}
          onConfirm={async function deleteBusinessUnit() {
            // Check if modal is open and has proper data
            if (!isDeletingSupportStaffRole) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingSupportStaffRole.supportStaffRoleId ===
              ISupportStaffRole.getDefault().supportStaffRoleId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingSupportStaffRole);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await ISupportStaffAPI.delete(isDeletingSupportStaffRole.supportStaffRoleId);
                // Update state after only successful delete
                removeRow(isDeletingSupportStaffRole);
                showToast('success', 'Successfully deleted support team role.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'support team role');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingSupportStaffRole(false);
          }}
        />
      )}
    </Page>
  );
};

function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
